/**
 * @fileoverview Per-template field label resolution.
 *
 * Purpose:
 * - Decide which label a placeholder shows when a template's own
 *   `extractedFields` metadata disagrees with the built-in canonical
 *   definition for the same key.
 *
 * Why the two diverge: the extraction prompt in
 * `convex/settings/precedents/actions.ts` forbids inventing keys and tells the
 * model to bind every placeholder to the CLOSEST canonical key, while the
 * accompanying `label` is taken from the document's own wording. A letter that
 * asks for books and records "by no later than {date}" therefore lands on
 * `report_return_date` — canonical label "Date ROCAP is Due from Director" —
 * carrying the template label "Delivery Due Date".
 *
 * The template library preview and the TipTap editor both render the template's
 * label (via `buildFieldsLabelMap`), so the generate wizard and the day-one
 * Edit Fields panel must too. Otherwise the practitioner is asked to fill a box
 * named after a field that appears nowhere in the letter in front of them.
 *
 * Used by:
 * - `_layout.jobs.$jobId.generate-precedents.tsx` — review inputs + preview chips
 * - `_layout.jobs.$jobId.day-one.tsx` — Edit Fields inputs + preview chips
 */

/** Shape of a `precedentTemplates.extractedFields` row we care about here. */
export interface TemplateFieldMetaRow {
  key: string;
  label?: string;
  description?: string;
  /** Set when the extractor normalised a document label onto a canonical key. */
  canonicalKey?: string;
}

export interface TemplateFieldMeta {
  label?: string;
  description?: string;
}

/**
 * Index a template's `extractedFields` by key so callers can overlay the
 * template's wording onto a canonical or custom field definition.
 *
 * `canonicalKey` is indexed alongside `key` for the same reason
 * `buildFieldsLabelMap` does it: older templates store the document's own key
 * and point at the canonical one separately.
 */
export function buildTemplateFieldMetaMap(
  fields?: ReadonlyArray<TemplateFieldMetaRow> | null,
): Map<string, TemplateFieldMeta> {
  const map = new Map<string, TemplateFieldMeta>();
  if (!fields) return map;
  for (const f of fields) {
    if (!f?.key) continue;
    const meta: TemplateFieldMeta = {
      label: f.label,
      description: f.description,
    };
    map.set(f.key, meta);
    if (f.canonicalKey) map.set(f.canonicalKey, meta);
  }
  return map;
}

/**
 * Overlay a template's own label / description onto a resolved field
 * definition. Blank overrides are ignored so a template that never named the
 * field keeps the canonical wording.
 *
 * Generic in `T` so callers keep whatever else they carry (`fieldType`,
 * `categoryId`, `placeholder`, …).
 */
export function preferTemplateFieldLabel<
  T extends { label: string; description?: string },
>(base: T, override?: TemplateFieldMeta): T {
  if (!override) return base;
  const label = override.label?.trim();
  const description = override.description?.trim();
  if (!label && !description) return base;
  return {
    ...base,
    label: label || base.label,
    description: description || base.description,
  };
}
